/* Public driver invitation acceptance (token-gated). Reached only via the single-use link
   in a driver invitation. Lets the invitee set a password; on success they're signed in and
   sent to the Driver Portal. No public self-registration exists. */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { api } from "@/lib/auth";
import { BrandLockup } from "@/components/Logo";

export default function DriverAccept() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setInfo(null); return; }
    api.get(`/driver/invitation/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post(`/driver/invitation/${token}/accept`, { password: password || undefined });
      nav("/driver");
    } catch (e2) {
      setErr(typeof e2.response?.data?.detail === "string" ? e2.response.data.detail : "Could not accept invitation");
      setBusy(false);
    }
  };

  const shell = (child) => (
    <main className="flex min-h-screen items-center justify-center bg-obsidian-900 px-6 py-24" data-testid="driver-accept">
      <div className="gold-frame w-full max-w-md rounded-3xl bg-obsidian-800 p-10 text-center"><BrandLockup className="mx-auto h-20 w-auto" />{child}</div>
    </main>
  );

  if (info === undefined) return shell(<Loader2 className="mx-auto mt-8 h-6 w-6 animate-spin text-gold-light" />);
  if (info === null) return shell(
    <><ShieldAlert className="mx-auto mt-8 h-10 w-10 text-gold" /><h1 className="mt-4 font-display text-2xl font-semibold text-ivory">Invitation unavailable</h1>
      <p className="mt-3 text-sm text-ivory/60" data-testid="driver-accept-invalid">This invitation is invalid, already used, or expired. Please ask your administrator to resend it.</p></>
  );

  const optional = info.has_account;
  return shell(
    <>
      <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Driver Invitation</p>
      <h1 className="mt-3 font-display text-2xl font-semibold text-ivory">Welcome{info.driver_name ? `, ${info.driver_name}` : ""}</h1>
      <p className="mt-2 text-sm text-ivory/60">{info.company} · <span data-testid="driver-accept-email">{info.email}</span></p>
      <form onSubmit={accept} className="mt-8 space-y-3 text-left">
        <label className="text-xs text-ivory/60">{optional ? "Set a new password (optional)" : "Create a password"}</label>
        <input data-testid="driver-accept-password" type="password" minLength={8} required={!optional} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 8 characters)"
          className="w-full rounded-lg border border-line bg-obsidian-900 px-3 py-2.5 text-sm text-ivory placeholder:text-dim-charcoal focus:border-gold/50 focus:outline-none" />
        {err && <p data-testid="driver-accept-error" className="text-xs text-red-400">{err}</p>}
        <button data-testid="driver-accept-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-obsidian-900 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Accept & Continue
        </button>
      </form>
    </>
  );
}
